import React, { useState, useLayoutEffect, useMemo } from "react";
import {
	Box,
	TextField,
	Checkbox,
	MenuItem,
	Select,
	Tooltip,
	FormControl,
	FormHelperText,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { responseTypes } from "../utils/constants";
import WarningChip from "../../../../Common/Chips/WarningChip";
import { isFunction } from "../../../../../utils/mappersUtils/guards";
import { GetSortedOptionsByName } from "../../../../../services/helpers";

// Custom styles for the description tooltip
const useDescriptionTooltipStyles = makeStyles({
	tooltip: {
		fontSize: "14px",
		maxWidth: "600px",
		padding: "10px",
		lineHeight: "1.4",
		whiteSpace: "normal",
	},
});

const useCustomSelectStyles = makeStyles({
	root: {
		padding: "3px 24px 7px 0px !important",
	},
});

export const SegmentItem = React.memo(function SegmentItem({
	containerId,
	itemId,
	isOverlay = false,
	isTranslationsPreview,
	isEditMode,
	segItem = {},
	translationsData,
	selectedLang,
	onItemBlur,
	onResponseTypeChange,
	onActiveChange,
	resources,
	touchedFields = {},
}) {
	const [shortValue, setShortValue] = useState("");
	const [longValue, setLongValue] = useState("");
	const [shortMissing, setShortMissing] = useState(false);
	const [longMissing, setLongMissing] = useState(false);
	const [shortTouched, setShortTouched] = useState(false);
	const [longTouched, setLongTouched] = useState(false);
	const [responseTouched, setResponseTouched] = useState(false);

	useLayoutEffect(() => {
		if (isTranslationsPreview) {
			const itemTranslationData = translationsData.find(
				(t) => t.type === "ITEM" && t.id === itemId
			);

			if (itemTranslationData?.translations?.[selectedLang]) {
				const group = itemTranslationData.translations[selectedLang].group;
				const shortTrans = group.find((obj) => obj.key === "shortDescription");
				const longTrans = group.find((obj) => obj.key === "description");

				if (shortTrans && shortTrans.value.trim()) {
					setShortValue(shortTrans.value);
					setShortMissing(false);
				} else {
					setShortValue(
						itemTranslationData.baseTranslations?.shortDescription || ""
					);
					setShortMissing(true);
				}

				if (longTrans && longTrans.value.trim()) {
					setLongValue(longTrans.value);
					setLongMissing(false);
				} else {
					setLongValue(itemTranslationData.baseTranslations?.description || "");
					setLongMissing(true);
				}
			} else {
				// No translation data found for a new item.
				setShortValue(segItem.shortDescription || "");
				setLongValue(segItem.longDescription || "");
				setShortMissing(true);
				setLongMissing(true);
			}
		} else {
			setShortValue(segItem.shortDescription || "");
			setLongValue(segItem.longDescription || "");
			setShortMissing(false);
			setLongMissing(false);
		}
	}, [segItem, itemId, isTranslationsPreview, selectedLang, translationsData]);

	const handleShortChange = (e) => {
		setShortValue(e.target.value.replace(/^\s+/, ""));
	};

	const handleLongChange = (e) => {
		setLongValue(e.target.value.replace(/^\s+/, ""));
	};

	const handleShortBlur = () => {
		setShortTouched(true);
		if (isFunction(onItemBlur)) {
			onItemBlur(containerId, itemId, "shortDescription", shortValue);
		}
	};

	const handleLongBlur = () => {
		setLongTouched(true);
		if (isFunction(onItemBlur)) {
			onItemBlur(containerId, itemId, "longDescription", longValue);
		}
	};

	const effectiveShortTouched = touchedFields.shortDescription || shortTouched;
	const effectiveLongTouched = touchedFields.longDescription || longTouched;
	const effectiveResponseTouched =
		touchedFields.responseType || responseTouched;

	// Calculate error states
	const shortError = effectiveShortTouched && !shortValue.trim();
	const longError = effectiveLongTouched && !longValue.trim();
	const responseError =
		effectiveResponseTouched &&
		(!segItem.responseType || segItem.responseType === 0);

	const showPlainResponseType = useMemo(() => {
		return (isEditMode && typeof itemId === "number") || isTranslationsPreview;
	}, [isEditMode, itemId, isTranslationsPreview]);

	const tooltipClasses = useDescriptionTooltipStyles();
	const customSelectClasses = useCustomSelectStyles();

	// Helper function to determine if text is likely to be truncated
	const isLikelyTruncated = (text, maxWidth) => {
		const averageCharWidth = 7; // pixels per character (approximate)
		const estimatedTextWidth = text.length * averageCharWidth;
		return estimatedTextWidth > maxWidth;
	};

	const longDescMaxWidth = 550;
	const shouldShowTooltip = isLikelyTruncated(longValue, longDescMaxWidth);

	return (
		<div className="d-flex justify-content-between w-100 align-items-center">
			<div
				className="d-flex flex-column w-100"
				style={{ gap: "8px", marginRight: "16px" }}
			>
				<div className="d-flex justify-content-between">
					{isTranslationsPreview ? (
						<Box display="flex" alignItems="center">
							<span
								id={`item-short-description-${containerId}-${itemId}${
									isOverlay ? "-overlay" : ""
								}`}
								style={{
									padding: "6px 8px",
									whiteSpace: "normal",
								}}
							>
								{shortValue}
							</span>
							{shortMissing && (
								<Tooltip title={resources["TranslationMissing"]}>
									<WarningChip label={resources["TranslationMissing"]} />
								</Tooltip>
							)}
						</Box>
					) : (
						<TextField
							required
							id={`item-short-description-${containerId}-${itemId}${
								isOverlay ? "-overlay" : ""
							}`}
							placeholder={resources["ShortDescription"]}
							size="small"
							value={shortValue}
							onChange={handleShortChange}
							onBlur={handleShortBlur}
							inputProps={{ maxLength: 40 }}
							error={shortError}
							helperText={shortError ? resources["FieldRequired"] : ""}
						/>
					)}
					{showPlainResponseType ? (
						<span className="color-grey align-self-center">
							{
								responseTypes(resources).find(
									(rt) => rt.value === segItem.responseType
								)?.name
							}
						</span>
					) : (
						<FormControl error={responseError} required style={{ width: 180 }}>
							<Select
								inputProps={{
									id: `item-response-type-${containerId}-${itemId}${
										isOverlay ? "-overlay" : ""
									}`,
								}}
								value={segItem.responseType ?? 0}
								onChange={(e) => {
									if (isFunction(onResponseTypeChange)) {
										onResponseTypeChange(containerId, itemId, e.target.value);
									}
								}}
								onBlur={() => setResponseTouched(true)}
								classes={{ root: customSelectClasses.root }}
								displayEmpty
							>
								<MenuItem value={0} disabled>
									<em>{resources["SelectResponseType"]}</em>
								</MenuItem>
								{GetSortedOptionsByName(responseTypes(resources)).map((rt) => (
									<MenuItem key={rt.value} value={rt.value}>
										{rt.name}
									</MenuItem>
								))}
							</Select>
							{responseError && (
								<FormHelperText>{resources["FieldRequired"]}</FormHelperText>
							)}
						</FormControl>
					)}
				</div>
				{isTranslationsPreview ? (
					<Box display="flex" alignItems="center">
						{shouldShowTooltip ? (
							<Tooltip
								title={longValue}
								arrow
								classes={{ tooltip: tooltipClasses.tooltip }}
							>
								<span
									id={`item-long-description-${containerId}-${itemId}${
										isOverlay ? "-overlay" : ""
									}`}
									style={{
										padding: "6px 8px",
										display: "inline-block",
										maxWidth: `${longDescMaxWidth}px`,
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
								>
									{longValue}
								</span>
							</Tooltip>
						) : (
							<span
								id={`item-long-description-${containerId}-${itemId}${
									isOverlay ? "-overlay" : ""
								}`}
								style={{
									padding: "6px 8px",
									display: "inline-block",
									maxWidth: `${longDescMaxWidth}px`,
									overflow: "hidden",
									textOverflow: "ellipsis",
									whiteSpace: "nowrap",
								}}
							>
								{longValue}
							</span>
						)}
						{longMissing && (
							<Tooltip title={resources["TranslationMissing"]}>
								<WarningChip label={resources["TranslationMissing"]} />
							</Tooltip>
						)}
					</Box>
				) : (
					<TextField
						id={`item-long-description-${containerId}-${itemId}${
							isOverlay ? "-overlay" : ""
						}`}
						placeholder={resources["LongDescription"]}
						size="small"
						value={longValue}
						onChange={handleLongChange}
						onBlur={handleLongBlur}
						inputProps={{ maxLength: 256 }}
						required
						error={longError}
						helperText={longError ? resources["FieldRequired"] : ""}
					/>
				)}
			</div>
			{!isTranslationsPreview && (
				<Tooltip title={resources["ItemIsActiveTooltip"]}>
					<Checkbox
						id={`item-active-${containerId}-${itemId}${
							isOverlay ? "-overlay" : ""
						}`}
						checked={segItem.isActive ?? true}
						onChange={(e) => {
							if (isFunction(onActiveChange)) {
								onActiveChange(containerId, itemId, e.target.checked);
							}
						}}
						style={{ padding: "6px" }}
					/>
				</Tooltip>
			)}
		</div>
	);
});
